import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Chip, Input, Textarea, Tooltip } from "@nextui-org/react";
import { IoWarningOutline } from "react-icons/io5";
import { LuSparkles, LuFlaskConical, LuX } from "react-icons/lu";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/Modal";
import myAxios from "../../../utils/axiosWrapper";
import { WeightDistributionBar } from "../GradingRulesPanel";
import InlineAudioPlayer from "../../debriefs/InlineAudioPlayer";

const FIELD_TYPE_LABELS = {
  bool: "Yes/No",
  number: "Note (0-10)",
  freetext: "Texte libre",
  category: "Catégorie",
};

const FIELD_TYPE_CHIP_COLORS = {
  bool: "success",
  number: "primary",
  freetext: "default",
  category: "secondary",
};

const DIRECTION_LABELS = {
  all: "Tous les appels",
  incoming: "Appels entrants",
  outgoing: "Appels sortants",
};

const GRADE_COLORS = {
  green: "text-emerald-600",
  orange: "text-amber-600",
  red: "text-red-600",
  gray: "text-gray-400",
};

const gradeColor = (grade) => {
  if (grade === null || grade === undefined || grade < 0) return GRADE_COLORS.gray;
  if (grade >= 7) return GRADE_COLORS.green;
  if (grade >= 5) return GRADE_COLORS.orange;
  return GRADE_COLORS.red;
};

const formatGrade = (grade) =>
  grade === null || grade === undefined || grade < 0 ? "–" : Number(grade).toFixed(1);

const formatValue = (value) => {
  if (value === null || value === undefined || value === "None") return "–";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// Firestore does not keep map key order: compare drafts / payloads on a
// key-sorted serialisation.
const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

// Drafts carry an `include` flag per item; the API only receives included ones.
// The admin's edits are autosaved on the proposal (`draft`) and win over the
// generated version.
const toDraft = (proposal) =>
  proposal?.draft || {
    fields: (proposal?.proposal?.fields || []).map((field) => ({ ...field, include: true })),
    grading_rules: (proposal?.proposal?.grading_rules || []).map((rule) => ({
      ...rule,
      // Without existing rules, applying one switches the whole company to rule
      // grading: opt-in only.
      include: !(proposal?.warnings || []).some((w) => w.code === "no_existing_rules"),
    })),
  };

const toPayload = (draft) => ({
  fields: draft.fields.filter((f) => f.include).map(({ include, ...field }) => field),
  grading_rules: draft.grading_rules.filter((r) => r.include).map(({ include, ...rule }) => rule),
});

const Section = ({ title, subtitle, children, right = null }) => (
  <section className="mt-6">
    <div className="flex items-end justify-between gap-3 mb-3">
      <div>
        <h3 className="text-base font-semibold text-[#171717]">{title}</h3>
        {subtitle && <p className="text-xs text-[#737373] mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

const FieldCard = ({ field, onChange, isReadOnly, fieldsByName, t }) => {
  const [showWhy, setShowWhy] = useState(false);
  return (
    <div
      className={`border rounded-xl p-4 transition-opacity ${
        field.include ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100 opacity-60"
      }`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          isSelected={field.include}
          onValueChange={(include) => onChange({ include })}
          isDisabled={isReadOnly}
          aria-label={t("Inclure")}
          className="mt-1"
        />
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              size="sm"
              value={field.display_name}
              onValueChange={(display_name) => onChange({ display_name })}
              isReadOnly={isReadOnly || !field.include}
              aria-label={t("Libellé")}
              className="max-w-xs"
              classNames={{ input: "font-medium" }}
            />
            <code className="text-[11px] text-[#737373] bg-gray-100 rounded px-1.5 py-0.5">
              {field.name}
            </code>
            <Chip
              size="sm"
              variant="flat"
              color={FIELD_TYPE_CHIP_COLORS[field.type]}
            >
              {t(FIELD_TYPE_LABELS[field.type])}
            </Chip>
            <Chip
              size="sm"
              variant="flat"
            >
              {t(DIRECTION_LABELS[field.call_direction])}
            </Chip>
            {field.trigger_field && (
              <Tooltip content={t("Évalué seulement si ce champ vaut Oui")}>
                <Chip
                  size="sm"
                  variant="flat"
                  color="warning"
                >
                  {t("si")}{" "}
                  {fieldsByName[field.trigger_field]?.display_name ||
                    field.trigger_field.replace(/_/g, " ")}
                </Chip>
              </Tooltip>
            )}
          </div>

          <Textarea
            size="sm"
            minRows={2}
            label={t("Consigne pour l'IA")}
            value={field.instructions}
            onValueChange={(instructions) => onChange({ instructions })}
            isReadOnly={isReadOnly || !field.include}
          />

          {field.type === "category" && (
            <Input
              size="sm"
              label={t("Catégories (séparées par ;)")}
              value={(field.categories || []).join("; ")}
              onValueChange={(value) =>
                onChange({
                  categories: value
                    .split(";")
                    .map((c) => c.trim())
                    .filter(Boolean),
                })
              }
              isReadOnly={isReadOnly || !field.include}
            />
          )}

          <div className="text-xs text-[#525252] bg-violet-50 border border-violet-100 rounded-lg p-3">
            <span className="font-medium text-violet-700">{t("Pourquoi")} : </span>
            {field.rationale}
            {field.evidence && (
              <>
                {" "}
                <button
                  type="button"
                  className="text-violet-600 hover:underline"
                  onClick={() => setShowWhy(!showWhy)}
                >
                  {showWhy ? t("Masquer les observations") : t("Voir les observations")}
                </button>
                {showWhy && <p className="mt-2 text-[#737373]">{field.evidence}</p>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RuleCard = ({ rule, onChange, isReadOnly, fieldsByName, existingFieldNames, t }) => {
  const updateComponent = (index, updates) =>
    onChange({
      components: rule.components.map((component, i) =>
        i === index ? { ...component, ...updates } : component
      ),
    });

  return (
    <div
      className={`border rounded-xl p-4 ${
        rule.include ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100 opacity-60"
      }`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          isSelected={rule.include}
          onValueChange={(include) => onChange({ include })}
          isDisabled={isReadOnly}
          aria-label={t("Inclure")}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              size="sm"
              value={rule.name}
              onValueChange={(name) => onChange({ name })}
              isReadOnly={isReadOnly || !rule.include}
              aria-label={t("Nom de la règle")}
              className="max-w-sm"
              classNames={{ input: "font-medium" }}
            />
            <Chip
              size="sm"
              variant="flat"
              color="secondary"
            >
              {t(DIRECTION_LABELS[rule.call_direction])}
            </Chip>
            <Chip
              size="sm"
              variant="flat"
              color="primary"
            >
              {t("Priorité maximale pour ce département")}
            </Chip>
          </div>

          <WeightDistributionBar components={rule.components} />

          <div className="mt-3 divide-y divide-gray-100">
            {rule.components.map((component, index) => {
              const field = fieldsByName[component.field_name];
              const isExisting = existingFieldNames.has(component.field_name);
              const isMissing = !isExisting && (!field || !field.include);
              return (
                <div
                  key={component.field_name}
                  className="flex items-center gap-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${isMissing ? "text-red-600" : "text-[#171717]"}`}>
                      {field?.display_name || component.field_name.replace(/_/g, " ")}
                    </span>
                    <span className="ml-2 text-[11px] text-[#A3A3A3]">
                      {isExisting ? t("champ existant") : t("nouveau champ")}
                    </span>
                    {component.bool_inverted && (
                      <Chip
                        size="sm"
                        variant="flat"
                        color="danger"
                        className="ml-2"
                      >
                        {t("malus")}
                      </Chip>
                    )}
                    {component.trigger_field && (
                      <span className="ml-2 text-[11px] text-amber-600">
                        {t("si")} {component.trigger_field.replace(/_/g, " ")}
                      </span>
                    )}
                    {isMissing && (
                      <p className="text-[11px] text-red-600">
                        {t("Ce champ n'est pas inclus : réincluez-le ou retirez ce composant.")}
                      </p>
                    )}
                  </div>
                  <Input
                    size="sm"
                    type="number"
                    min={1}
                    max={10}
                    value={String(component.weight)}
                    onValueChange={(value) =>
                      updateComponent(index, {
                        weight: Math.max(1, Math.min(10, parseInt(value, 10) || 1)),
                      })
                    }
                    isReadOnly={isReadOnly || !rule.include}
                    aria-label={t("Poids")}
                    className="w-20"
                    startContent={<span className="text-[11px] text-[#A3A3A3]">×</span>}
                  />
                  {!isReadOnly && rule.include && (
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      aria-label={t("Retirer")}
                      onClick={() =>
                        onChange({
                          components: rule.components.filter((_, i) => i !== index),
                        })
                      }
                    >
                      <LuX size={14} />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 text-xs text-[#525252] bg-violet-50 border border-violet-100 rounded-lg p-3">
            <span className="font-medium text-violet-700">{t("Pourquoi")} : </span>
            {rule.rationale}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatTimecode = (seconds) => {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// Summary, chapters and audio of a test call, loaded on first expand (kept
// out of the proposal document: the dry run only stores what the test needs).
const DryRunCallDetails = ({ apiUrl, proposalId, call, cache, onLoaded, t }) => {
  const playerRef = useRef(null);
  const details = cache[call.call_id];
  const [error, setError] = useState(null);

  useEffect(() => {
    if (details || !call.call_id) return;
    myAxios
      .get(
        `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals/${proposalId}/calls/${call.call_id}`
      )
      .then((response) => onLoaded(call.call_id, response.data))
      .catch(() => setError(t("Impossible de charger le détail de l'appel.")));
  }, [call.call_id]);

  const overview = details?.overview || call.overview;
  const chapters = details?.chapters || [];

  return (
    <div className="space-y-3">
      {details?.audio_url && (
        <InlineAudioPlayer
          ref={playerRef}
          src={details.audio_url}
        />
      )}
      {!details && !error && (
        <p className="text-xs text-[#A3A3A3]">{t("Chargement du détail de l'appel…")}</p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      {details?.operator_name && (
        <p className="text-xs text-[#737373]">
          {t("Opérateur")} : {details.operator_name}
        </p>
      )}
      {overview && (
        <div>
          <p className="text-xs font-medium text-[#404040] mb-1">{t("Résumé")}</p>
          <p className="text-xs text-[#525252] whitespace-pre-line">{overview}</p>
        </div>
      )}
      {chapters.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[#404040] mb-1">{t("Chapitres")}</p>
          <div className="space-y-2">
            {chapters.map((chapter, chapterIndex) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={chapterIndex}
                className="text-xs"
              >
                <div className="flex items-center gap-2">
                  {typeof chapter.start === "number" && details?.audio_url && (
                    <button
                      type="button"
                      onClick={() => playerRef.current?.seekTo(chapter.start)}
                      className="text-[11px] font-mono text-primary bg-primary/10 rounded px-1.5 py-0.5 hover:bg-primary/20"
                    >
                      ▶ {formatTimecode(chapter.start)}
                    </button>
                  )}
                  <span className="font-medium text-[#262626]">{chapter.title}</span>
                </div>
                {chapter.points?.length > 0 && (
                  <ul className="mt-1 ml-4 list-disc text-[#525252] space-y-0.5">
                    {chapter.points.map((point, pointIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={pointIndex}>{point}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DryRunTable = ({ apiUrl, proposalId, dryRun, fieldsByName, t }) => {
  const [expanded, setExpanded] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const onDetailsLoaded = (callId, details) =>
    setDetailsCache((previous) => ({ ...previous, [callId]: details }));
  if (!dryRun?.length) {
    return <p className="text-sm text-[#737373]">{t("Aucun appel de test disponible.")}</p>;
  }
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {dryRun.map((call, index) => {
        const isOpen = expanded === index;
        const newGrade = call.new_grade?.call_overall_grade;
        return (
          <div
            key={call.call_id || index}
            className="border-b last:border-b-0 border-gray-100"
          >
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : index)}
              className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-gray-50"
            >
              <span className="text-xs text-[#737373] w-24 shrink-0">
                {call.call_timestamp ? new Date(call.call_timestamp).toLocaleDateString() : ""}
              </span>
              <span className="text-xs w-20 shrink-0">
                {t(call.direction === "incoming" ? "Entrant" : "Sortant")}
              </span>
              <span className="text-sm flex-1 truncate">{call.purpose || "–"}</span>
              <span className="text-xs text-[#737373]">{t("Note actuelle")}</span>
              <span className={`text-sm font-semibold w-10 ${gradeColor(call.current_grade)}`}>
                {formatGrade(call.current_grade)}
              </span>
              <span className="text-xs text-[#737373]">→</span>
              <span className={`text-lg font-bold w-12 ${gradeColor(newGrade)}`}>
                {formatGrade(newGrade)}
              </span>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 space-y-3">
                {call.error && (
                  <p className="text-xs text-red-600">
                    {t("Échec du test sur cet appel")} : {call.error}
                  </p>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <DryRunCallDetails
                    apiUrl={apiUrl}
                    proposalId={proposalId}
                    call={call}
                    cache={detailsCache}
                    onLoaded={onDetailsLoaded}
                    t={t}
                  />
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-2">
                      {Object.entries(call.values || {}).map(([name, value]) => (
                        <div
                          key={name}
                          className="flex items-start justify-between gap-3 text-xs bg-gray-50 rounded-lg px-3 py-2"
                        >
                          <span className="text-[#525252]">
                            {fieldsByName[name]?.display_name || name.replace(/_/g, " ")}
                          </span>
                          <span className="font-medium text-[#171717] text-right">
                            {formatValue(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {call.new_grade?.items_breakdown && (
                      <p className="text-[11px] text-[#A3A3A3]">
                        {t("Règle appliquée")} : {call.new_grade.grading_rule_name} ·{" "}
                        {call.new_grade.items_breakdown
                          .map(
                            (item) =>
                              `${item.field_name.replace(/_/g, " ")}: ${
                                item.status === "ok" ? item.score : item.status
                              }`
                          )
                          .join(" · ")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ProposalPreview = ({
  apiUrl,
  proposal,
  existingFields,
  blockingReason,
  onApplied,
  onRejected,
  onClose,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => toDraft(proposal));
  const [dryRun, setDryRun] = useState(proposal?.dry_run || []);
  // The payload the displayed dry run was computed on: the whole generated
  // proposal, until the admin re-runs it on an edited selection.
  const initialDryRunSnapshot = (p) =>
    stableStringify(
      p?.dry_run_payload || {
        fields: p?.proposal?.fields || [],
        grading_rules: p?.proposal?.grading_rules || [],
      }
    );
  const [dryRunSnapshot, setDryRunSnapshot] = useState(() => initialDryRunSnapshot(proposal));
  const [isTesting, setIsTesting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [apiErrors, setApiErrors] = useState([]);
  const [draftSaveState, setDraftSaveState] = useState("saved");
  const lastSavedDraftRef = useRef(stableStringify(toDraft(proposal)));

  useEffect(() => {
    const initialDraft = toDraft(proposal);
    setDraft(initialDraft);
    setDryRun(proposal?.dry_run || []);
    setDryRunSnapshot(initialDryRunSnapshot(proposal));
    setApiErrors([]);
    setDraftSaveState("saved");
    lastSavedDraftRef.current = stableStringify(initialDraft);
  }, [proposal?.id]);

  const isReadOnly = proposal?.status !== "pending";
  const payload = useMemo(() => toPayload(draft), [draft]);
  const isDryRunStale = !isReadOnly && stableStringify(payload) !== dryRunSnapshot;

  // Autosave the draft, debounced, so a refresh keeps the edits.
  useEffect(() => {
    if (isReadOnly) return undefined;
    const serialized = stableStringify(draft);
    if (serialized === lastSavedDraftRef.current) return undefined;
    setDraftSaveState("pending");
    const timer = setTimeout(async () => {
      setDraftSaveState("saving");
      try {
        await myAxios.put(
          `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals/${proposal.id}/draft`,
          draft
        );
        lastSavedDraftRef.current = serialized;
        setDraftSaveState("saved");
      } catch (error) {
        setDraftSaveState("error");
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [draft, isReadOnly]);

  const fieldsByName = useMemo(() => {
    const map = {};
    (existingFields || []).forEach((field) => {
      map[field.name] = { ...field, include: true };
    });
    draft.fields.forEach((field) => {
      map[field.name] = field;
    });
    return map;
  }, [draft.fields, existingFields]);

  const existingFieldNames = useMemo(
    () => new Set((existingFields || []).map((field) => field.name)),
    [existingFields]
  );

  const hasDanglingComponent = payload.grading_rules.some((rule) =>
    rule.components.some(
      (component) =>
        !existingFieldNames.has(component.field_name) &&
        !payload.fields.some((field) => field.name === component.field_name)
    )
  );
  const hasTooFewComponents = payload.grading_rules.some((rule) => rule.components.length < 2);
  const isEmpty = !payload.fields.length && !payload.grading_rules.length;

  const updateItem = (kind, index) => (updates) =>
    setDraft((previous) => ({
      ...previous,
      [kind]: previous[kind].map((item, i) => (i === index ? { ...item, ...updates } : item)),
    }));

  const extractErrors = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail?.errors) return detail.errors;
    return [typeof detail === "string" ? detail : error.message];
  };

  const handleRetest = async () => {
    setIsTesting(true);
    setApiErrors([]);
    try {
      const response = await myAxios.post(
        `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals/${proposal.id}/dry_run`,
        payload
      );
      setDryRun(response.data.dry_run || []);
      setDryRunSnapshot(stableStringify(response.data.dry_run_payload || payload));
    } catch (error) {
      setApiErrors(extractErrors(error));
    } finally {
      setIsTesting(false);
    }
  };

  const handleApply = async () => {
    setIsApplying(true);
    setApiErrors([]);
    try {
      await myAxios.post(
        `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals/${proposal.id}/apply`,
        payload
      );
      setIsConfirmOpen(false);
      onApplied();
    } catch (error) {
      setIsConfirmOpen(false);
      setApiErrors(extractErrors(error));
    } finally {
      setIsApplying(false);
    }
  };

  const handleReject = async () => {
    try {
      await myAxios.post(
        `${apiUrl}/audio/api/v1/ai_feature/department_config/proposals/${proposal.id}/reject`
      );
      onRejected();
    } catch (error) {
      setApiErrors(extractErrors(error));
    }
  };

  const stats = proposal?.sample_stats || {};
  const profile = proposal?.proposal || {};
  const warnings = proposal?.warnings || [];
  const applyDisabledReason =
    blockingReason ||
    (hasDanglingComponent && t("Une règle utilise un champ non inclus.")) ||
    (hasTooFewComponents && t("Une règle doit garder au moins 2 composants.")) ||
    (isEmpty && t("Aucun élément sélectionné."));

  return (
    <div className="mt-6 border border-gray-200 rounded-2xl bg-[#FAFAFA] p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <LuSparkles
              className="text-violet-600"
              size={18}
            />
            <h2 className="text-lg font-semibold text-[#171717]">
              {t("Proposition pour")} {proposal.department_name}
            </h2>
            {isReadOnly && (
              <Chip
                size="sm"
                variant="flat"
                color={proposal.status === "applied" ? "success" : "default"}
              >
                {t(proposal.status === "applied" ? "Appliquée" : "Rejetée")}
              </Chip>
            )}
          </div>
          <p className="text-xs text-[#737373] mt-1">
            {t(DIRECTION_LABELS[proposal.call_direction])} · {stats.size} {t("appels analysés")}{" "}
            {t("sur")} {stats.candidates_count ?? "?"} · {stats.operators_count} {t("opérateurs")}
            {stats.first_call_at &&
              ` · ${new Date(stats.first_call_at).toLocaleDateString()} → ${new Date(
                stats.last_call_at
              ).toLocaleDateString()}`}
          </p>
          <div className="flex gap-1.5 flex-wrap mt-2">
            {Object.entries(stats.by_purpose || {}).map(([purpose, count]) => (
              <Chip
                key={purpose}
                size="sm"
                variant="bordered"
              >
                {purpose} · {count}
              </Chip>
            ))}
          </div>
        </div>
        <Button
          size="sm"
          variant="light"
          onClick={onClose}
        >
          {t("Fermer")}
        </Button>
      </div>

      {warnings.length > 0 && (
        <div className="mt-4 space-y-2">
          {warnings.map((warning, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3"
            >
              <IoWarningOutline
                size={16}
                className="shrink-0 mt-0.5"
              />
              {warning.message}
            </div>
          ))}
        </div>
      )}

      <Section title={t("Profil du département")}>
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-[#262626] whitespace-pre-line">{profile.department_profile}</p>
          {profile.call_types?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {profile.call_types.map((callType) => (
                <Tooltip
                  key={callType.label}
                  content={callType.description}
                >
                  <Chip
                    size="sm"
                    variant="flat"
                    color="secondary"
                  >
                    {callType.label} {callType.share}
                  </Chip>
                </Tooltip>
              ))}
            </div>
          )}
          <div className="text-sm text-[#404040] border-t border-gray-100 pt-3 whitespace-pre-line">
            <span className="font-medium">{t("Logique de la proposition")} : </span>
            {profile.explanation}
          </div>
        </div>
      </Section>

      <Section
        title={t("Champs d'analyse proposés")}
        subtitle={t(
          "Ajoutés pour ce département uniquement. Les appels déjà analysés ne sont pas recalculés."
        )}
      >
        <div className="space-y-3">
          {draft.fields.map((field, index) => (
            <FieldCard
              key={field.name}
              field={field}
              onChange={updateItem("fields", index)}
              isReadOnly={isReadOnly}
              fieldsByName={fieldsByName}
              t={t}
            />
          ))}
        </div>
      </Section>

      <Section
        title={t("Règles de notation proposées")}
        subtitle={t("Note = (somme des scores normalises x poids) / (somme des poids) x 10")}
      >
        {draft.grading_rules.length === 0 ? (
          <p className="text-sm text-[#737373]">{t("Aucune règle proposée.")}</p>
        ) : (
          <div className="space-y-3">
            {draft.grading_rules.map((rule, index) => (
              <RuleCard
                key={rule.call_direction}
                rule={rule}
                onChange={updateItem("grading_rules", index)}
                isReadOnly={isReadOnly}
                fieldsByName={fieldsByName}
                existingFieldNames={existingFieldNames}
                t={t}
              />
            ))}
          </div>
        )}
      </Section>

      <Section
        title={t("Test sur des appels réels")}
        subtitle={t(
          "Les consignes sont appliquées à des transcripts non utilisés pour la génération. Résultat indicatif : le pipeline utilise un autre modèle."
        )}
        right={
          !isReadOnly && (
            <Button
              size="sm"
              variant={isDryRunStale ? "solid" : "flat"}
              color={isDryRunStale ? "primary" : "default"}
              startContent={!isTesting && <LuFlaskConical size={14} />}
              onClick={handleRetest}
              isLoading={isTesting}
              isDisabled={isEmpty || hasDanglingComponent || hasTooFewComponents}
            >
              {isDryRunStale ? t("Relancer le test avec les modifications") : t("Relancer le test")}
            </Button>
          )
        }
      >
        <DryRunTable
          apiUrl={apiUrl}
          proposalId={proposal.id}
          dryRun={dryRun}
          fieldsByName={fieldsByName}
          t={t}
        />
      </Section>

      {apiErrors.length > 0 && (
        <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
          {apiErrors.map((error) => (
            <p key={error}>{error}</p>
          ))}
        </div>
      )}

      {!isReadOnly && (
        <div className="sticky bottom-4 mt-6 flex items-center justify-between gap-3 bg-[#171717] text-white rounded-xl px-4 py-3 shadow-lg flex-wrap">
          <span className="text-sm">
            {payload.fields.length} {t("champ(s)")} · {payload.grading_rules.length} {t("règle(s)")}{" "}
            {t("sélectionné(s)")}
            <span
              className={`ml-2 text-xs ${
                draftSaveState === "error" ? "text-red-300" : "text-white/50"
              }`}
            >
              ·{" "}
              {
                {
                  saved: t("Brouillon enregistré"),
                  pending: t("Modifications non enregistrées"),
                  saving: t("Enregistrement…"),
                  error: t("Échec de l'enregistrement du brouillon"),
                }[draftSaveState]
              }
            </span>
            {applyDisabledReason && (
              <span className="ml-2 text-amber-300">· {applyDisabledReason}</span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="flat"
              className="bg-white/10 text-white"
              onClick={handleReject}
            >
              {t("Rejeter")}
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={() => setIsConfirmOpen(true)}
              isDisabled={Boolean(applyDisabledReason)}
            >
              {t("Appliquer la sélection")}
            </Button>
          </div>
        </div>
      )}

      <Modal
        isOpen={isConfirmOpen}
        handleOnClose={() => setIsConfirmOpen(false)}
        showFooter={false}
        size="lg"
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-[#171717]">
            {t("Appliquer à la configuration de production ?")}
          </h3>
          <ul className="mt-3 text-sm text-[#404040] list-disc pl-5 space-y-1">
            <li>
              {payload.fields.length} {t("champ(s) d'analyse ajouté(s) au département")}{" "}
              {proposal.department_name}
            </li>
            {payload.grading_rules.map((rule) => (
              <li key={rule.call_direction}>
                {t("Règle")} « {rule.name} » {t("placée en priorité n°1")}
              </li>
            ))}
            <li>{t("Effet sur les prochains appels analysés uniquement.")}</li>
          </ul>
          {warnings
            .filter((w) => ["no_existing_rules", "custom_grading_overrides"].includes(w.code))
            .filter(() => payload.grading_rules.length > 0)
            .map((warning) => (
              <p
                key={warning.code}
                className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3"
              >
                {warning.message}
              </p>
            ))}
          {isDryRunStale && (
            <p className="mt-3 text-xs text-[#737373]">
              {t("Le test n'a pas été relancé depuis vos modifications.")}
            </p>
          )}
          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="flat"
              onClick={() => setIsConfirmOpen(false)}
            >
              {t("Annuler")}
            </Button>
            <Button
              color="primary"
              onClick={handleApply}
              isLoading={isApplying}
            >
              {t("Appliquer")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProposalPreview;
